/**
 * DForge usage tracking for SmartTrader sites. Sends named events (visits,
 * Deriv logins, trades, rejections, disconnects) to the DForge collector at
 * dforge.site/api/ue, which stores them per site so DForge can see what
 * traders use and what fails, across every site on this build. The same
 * module runs on every DForge trading template (multi_platforms has the
 * original); only APP differs.
 *
 * Rules this module keeps:
 * - It never throws and never blocks the page: every call is fire-and-forget.
 * - It never sends tokens, login ids, balances or anything a trader typed.
 *   Only the event name, the path, the tab and a few flat props leave the
 *   browser, and the collector drops sensitive keys again.
 * - Trades are counted here and sent as one `trade_placed` event per minute
 *   with a count, and repeats of the same event (a rejection code, a
 *   disconnect, an error) are sent at most once a minute, so a busy site
 *   cannot flood the collector.
 *
 * Events are queued and sent in batches every 5 seconds, at 20 events, and
 * with sendBeacon when the tab is hidden or closed (a login redirect, for
 * example), so leaving a page doesn't lose its last events.
 */
const DforgeUsage = (() => {
    const USAGE_ENDPOINT   = 'https://dforge.site/api/ue';
    const APP              = 'smarttrader';
    const FLUSH_MS         = 5000;
    const FLUSH_AT         = 20;
    const TRADE_WINDOW_MS  = 60000;
    const THROTTLE_MS      = 60000;
    const SESSION_IDLE_MS  = 30 * 60 * 1000;

    const ANON_KEY    = 'dfu_anon';
    const SESSION_KEY = 'dfu_sid';
    const LAST_KEY    = 'dfu_last';

    let queue       = [];
    let timer       = null;
    let started     = false;
    let memory      = {};
    const last_sent = new Map();
    const skipped   = new Map();
    let trades      = { real: 0, demo: 0 };
    let trade_timer = null;

    // Tracking runs only in a browser on a real domain (not localhost).
    const enabled = () => {
        try {
            return typeof window !== 'undefined' && window.location.hostname.includes('.');
        } catch (e) {
            return false;
        }
    };

    const randomId = () => {
        try {
            return crypto.randomUUID().replace(/-/g, '');
        } catch (e) {
            return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
        }
    };

    const read = (key) => {
        try {
            return window.localStorage.getItem(key);
        } catch (e) {
            return memory[key] || null;
        }
    };

    const write = (key, value) => {
        try {
            window.localStorage.setItem(key, value);
        } catch (e) {
            memory[key] = value;
        }
    };

    const anonId = () => {
        let id = read(ANON_KEY);
        if (!id) {
            id = randomId();
            write(ANON_KEY, id);
        }
        return id;
    };

    const sessionId = (now) => {
        let id     = read(SESSION_KEY);
        const last = Number(read(LAST_KEY) || '0');
        if (!id || now - last > SESSION_IDLE_MS) {
            id = randomId();
            write(SESSION_KEY, id);
        }
        write(LAST_KEY, String(now));
        return id;
    };

    // The current page name (`/en/trading.html` → "trading"), or undefined.
    const currentTab = () => {
        try {
            const page = window.location.pathname.split('/').pop().replace(/\.html$/, '');
            return /^[a-z0-9_-]{1,40}$/i.test(page) ? page : undefined;
        } catch (e) {
            return undefined;
        }
    };

    const cleanProps = (props) => {
        if (!props) return undefined;
        const out = {};
        Object.keys(props).forEach((key) => {
            const value = props[key];
            if (typeof value === 'string') out[key] = value.slice(0, 200);
            else if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) {
                out[key] = value;
            }
        });
        return Object.keys(out).length ? out : undefined;
    };

    const send = (use_beacon) => {
        if (!queue.length) return;
        const events = queue;
        queue = [];
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        try {
            const body = JSON.stringify({
                app : APP,
                site: window.location.hostname,
                anon: anonId(),
                sid : sessionId(Date.now()),
                events,
            });
            if (use_beacon && typeof navigator.sendBeacon === 'function') {
                if (navigator.sendBeacon(USAGE_ENDPOINT, body)) return;
            }
            fetch(USAGE_ENDPOINT, {
                method     : 'POST',
                headers    : { 'Content-Type': 'text/plain' },
                body,
                keepalive  : true,
                credentials: 'omit',
            }).catch(() => {});
        } catch (e) {
            // Tracking must never break the page.
        }
    };

    // Queues one event.
    const trackUsage = (name, props) => {
        if (!enabled()) return;
        try {
            queue.push({ n: name, t: Date.now(), path: window.location.pathname, p: cleanProps(props) });
            if (queue.length >= FLUSH_AT) {
                send(false);
            } else if (!timer) {
                timer = setTimeout(() => send(false), FLUSH_MS);
            }
        } catch (e) {
            // Tracking must never break the page.
        }
    };

    /**
     * Queues an event at most once a minute per `key` (an error code, for
     * example). Repeats in between are counted and reported as `repeats` on
     * the next one that goes out.
     */
    const trackOnce = (name, key, props) => {
        if (!enabled()) return;
        const id   = `${name}:${key}`;
        const now  = Date.now();
        const last = last_sent.get(id);
        if (last !== undefined && now - last < THROTTLE_MS) {
            skipped.set(id, (skipped.get(id) || 0) + 1);
            return;
        }
        last_sent.set(id, now);
        const repeats = skipped.get(id) || 0;
        skipped.delete(id);
        trackUsage(name, repeats ? { ...props, repeats } : props);
    };

    const flushTrades = () => {
        if (trade_timer) {
            clearTimeout(trade_timer);
            trade_timer = null;
        }
        const { real, demo } = trades;
        if (!real && !demo) return;
        trades = { real: 0, demo: 0 };
        trackUsage('trade_placed', { count: real + demo, real, demo });
    };

    // Sends everything queued now, with sendBeacon (used when the page is leaving).
    const flushUsage = () => {
        flushTrades();
        send(true);
    };

    // Counts one bought contract; sent as a per-minute `trade_placed` total.
    const countTrade = (is_virtual) => {
        if (!enabled()) return;
        if (is_virtual) trades.demo++;
        else trades.real++;
        if (!trade_timer) trade_timer = setTimeout(flushTrades, TRADE_WINDOW_MS);
    };

    // A Deriv error code safe to store (letters, digits, "_" and "."), or "unknown".
    const errorCodeOf = (error) => {
        const code = error && typeof error === 'object' ? error.code : undefined;
        if (typeof code === 'string' && /^[A-Za-z0-9_.]{1,60}$/.test(code)) return code;
        // Socket wrappers often reject with new Error(`${code}: ${message}`).
        let message = '';
        if (error instanceof Error) message = error.message;
        else if (typeof error === 'string') message = error;
        const match = /^([A-Za-z][A-Za-z0-9_.]{1,59}):\s/.exec(message);
        return match ? match[1] : 'unknown';
    };

    /**
     * A readable message for a rejected promise. Deriv API calls reject with
     * plain objects ({ error: { code, message } }), which String() turns into
     * "[object Object]", so their code and message are used instead.
     */
    const describeRejection = (reason) => {
        if (reason instanceof Error) return reason.message || reason.name;
        if (reason && typeof reason === 'object') {
            const inner   = reason.error;
            const source  = inner && typeof inner === 'object' ? inner : reason;
            const code    = errorCodeOf(source);
            const message = typeof source.message === 'string' ? source.message : '';
            if (code !== 'unknown' || message) {
                return [code !== 'unknown' ? code : '', message].filter(Boolean).join(': ');
            }
            return 'object rejection';
        }
        return String(reason === undefined || reason === null ? 'rejection' : reason);
    };

    /**
     * Starts tracking: records the first visit, page errors, and flushes when
     * the page is hidden or closed. Later pjax page loads are recorded by
     * BinaryLoader. Safe to call twice.
     */
    const startUsageTracking = () => {
        if (started || !enabled()) return;
        started = true;
        try {
            trackUsage('page_view', { tab: currentTab() });
            window.addEventListener('error', (event) => {
                const message = String(event.message || 'error');
                trackOnce('client_error', message.slice(0, 80), { message, kind: 'error' });
            });
            window.addEventListener('unhandledrejection', (event) => {
                const message = describeRejection(event.reason);
                trackOnce('client_error', message.slice(0, 80), { message, kind: 'rejection' });
            });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') flushUsage();
            });
            window.addEventListener('pagehide', flushUsage);
        } catch (e) {
            // Tracking must never break the page.
        }
    };

    // Test seam: drop queued events, counters and in-memory ids.
    const resetUsage = () => {
        queue   = [];
        memory  = {};
        started = false;
        last_sent.clear();
        skipped.clear();
        trades = { real: 0, demo: 0 };
        if (timer) clearTimeout(timer);
        if (trade_timer) clearTimeout(trade_timer);
        timer       = null;
        trade_timer = null;
    };

    return {
        USAGE_ENDPOINT,
        countTrade,
        currentTab,
        describeRejection,
        errorCodeOf,
        flushUsage,
        resetUsage,
        startUsageTracking,
        trackOnce,
        trackUsage,
    };
})();

module.exports = DforgeUsage;
